import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { FeatureAccess } from '../../../../packages/auth/feature-access';

// Temporary simple auth client until Better Auth works with D1
const API_URL = import.meta.env.VITE_API_URL || "https://skybit-api.botnet-599.workers.dev";

export const signIn = {
  async email({ email, password, callbackURL }: { email: string; password: string; callbackURL?: string }) {
    const response = await fetch(`${API_URL}/api/auth/signin/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sign in');
    }
    
    const data = await response.json();
    if (callbackURL) {
      window.location.href = callbackURL;
    }
    return data;
  },
  
  async social({ provider, callbackURL }: { provider: 'google' | 'apple'; callbackURL?: string }) {
    // OAuth flow would redirect to provider
    // For now, show a message that OAuth is not yet implemented
    throw new Error(`OAuth with ${provider} is not yet implemented. Please use email/password for now.`);
  }
};

export const signUp = {
  async email({ email, password, callbackURL }: { email: string; password: string; callbackURL?: string }) {
    const response = await fetch(`${API_URL}/api/auth/signup/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sign up');
    }
    
    const data = await response.json();
    if (callbackURL) {
      window.location.href = callbackURL;
    }
    return data;
  }
};

export const signOut = async () => {
  // Call the signout endpoint to clear the HttpOnly cookie
  await fetch(`${API_URL}/api/auth/signout`, {
    method: 'POST',
    credentials: 'include'
  });
  
  // Force reload to clear any cached auth state
  window.location.href = '/';
};

// Custom hook for session management
function useBetterAuthSession() {
  const [session, setSession] = useState<any>(null);
  const [isPending, setIsPending] = useState(true);
  
  useEffect(() => {
    // Check if we have a session by calling the user endpoint
    fetch(`${API_URL}/api/user`, {
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && data.user) {
          setSession({ user: data.user });
        } else {
          setSession(null);
        }
        setIsPending(false);
      })
      .catch(() => {
        setSession(null);
        setIsPending(false);
      });
  }, []);
  
  return { data: session, isPending };
}

// Create a context for auth state
interface AuthContextType {
  isLoggedIn: boolean;
  user: any | null;
  features: FeatureAccess | null;
  isLoading: boolean;
  signIn: typeof signIn;
  signUp: typeof signUp;
  signOut: typeof signOut;
}

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  user: null,
  features: null,
  isLoading: true,
  signIn,
  signUp,
  signOut,
});

// Auth provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const session = useBetterAuthSession();
  const [authState, setAuthState] = useState<AuthContextType>({
    isLoggedIn: false,
    user: null,
    features: null,
    isLoading: true,
    signIn,
    signUp,
    signOut,
  });

  useEffect(() => {
    // Fetch user profile with features when session changes
    if (session.data?.user) {
      fetch(`${API_URL}/api/user`, {
        credentials: 'include'
      })
        .then(res => res.json())
        .then(data => {
          setAuthState({
            isLoggedIn: true,
            user: data.user,
            features: data.features,
            isLoading: false,
            signIn,
            signUp,
            signOut,
          });
        })
        .catch(() => {
          setAuthState({
            isLoggedIn: !!session.data,
            user: session.data?.user || null,
            features: null,
            isLoading: false,
            signIn,
            signUp,
            signOut,
          });
        });
    } else {
      setAuthState({
        isLoggedIn: false,
        user: null,
        features: null,
        isLoading: session.isPending,
        signIn,
        signUp,
        signOut,
      });
    }
  }, [session.data, session.isPending]);

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth state
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// Hook to get current session
export function useSession() {
  return useBetterAuthSession();
}